import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Service, { isServiceReady } from '../services/Service';
import UserService from '../services/UserService';
import { Field, Locale } from '../types/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@follow/ui';

const Login = () => {
  const [email, setEmail] = useState<Field>({ value: '' });
  const [password, setPassword] = useState<Field>({ value: '' });
  const [searchParams] = useSearchParams();
  const toastShown = useRef(false);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const resendVerificationMutation = useMutation({
    mutationFn: (email: string) => UserService.resendVerificationEmail(email),
    onSuccess: () => {
      toast.success('Email de vérification envoyé.');
    },
  });

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      UserService.login(email, password, Locale.FR),
    onSuccess: (response) => {
      if (response && 'accessToken' in response && 'refreshToken' in response) {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.removeItem('locationId');
        queryClient.clear();
        navigate('/loading');
      }
    },
    onError: (e: any) => {
      if (e.code === 'EMAIL_NOT_VERIFIED') {
        toast.error(
          (t) => (
            <div>
              <p>Veuillez vérifier votre email avant de vous connecter.</p>
              <button
                className="mt-2 underline text-sm"
                onClick={() => {
                  resendVerificationMutation.mutate(email.value!);
                  toast.dismiss(t.id);
                }}
              >
                Renvoyer l'email de vérification
              </button>
            </div>
          ),
          { duration: 8000 }
        );
      } else if (e.code === 'INVALID_CREDENTIALS') {
        toast.error('Email ou mot de passe incorrect');
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      }
    },
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail({ value: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword({ value: e.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const form = event.currentTarget;
      form.requestSubmit();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!loginMutation.isPending) {
      loginMutation.mutate({ email: email.value!, password: password.value! });
    }
  };

  useEffect(() => {
    if (!isServiceReady) {
      Service.init(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
    }
    queryClient.invalidateQueries({ queryKey: ['user'] });
  }, [navigate, queryClient]);

  useEffect(() => {
    if (toastShown.current) return;

    const verified = searchParams.get('verified');
    const reset = searchParams.get('reset');
    // Set by the invitation page when an existing account accepted an invite:
    // no session is minted on purpose, so they land here to sign in.
    const invited = searchParams.get('invited');

    if (!verified && !reset && !invited) return;

    // Delay to ensure Toaster is mounted on full page loads (e.g. email verification redirect)
    const timeout = setTimeout(() => {
      if (toastShown.current) return;

      if (verified === 'true') {
        toast.success('Email vérifié avec succès. Vous pouvez vous connecter.');
      } else if (verified === 'error') {
        toast.error('Le lien de vérification est invalide ou a expiré.');
      }

      if (reset === 'true') {
        toast.success('Votre mot de passe a été réinitialisé avec succès.');
      }

      if (invited === '1') {
        toast.success('Invitation acceptée. Connectez-vous pour accéder au site.');
      }

      toastShown.current = true;
      navigate('/login', { replace: true });
    }, 100);

    return () => clearTimeout(timeout);
  }, [navigate, searchParams]);

  return (
    <div className="flex min-h-screen bg-off-white overscroll-none overflow-hidden">
      {/* LEFT PANEL */}
      <div className="w-full lg:w-[35%] bg-secondary-900 flex flex-col">
        <div className="p-8">
          <a href={process.env.REACT_APP_LANDING_URL || '/'}>
            <img src="/logo_full_light.svg" alt="Follow" className="h-7 w-auto" />
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 lg:px-16">
          <form className="w-full max-w-[448px]" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <h1 className="text-white/80 typo-h1">Bon retour</h1>
            <p className="text-white/70 typo-body-lg mt-2">Connectez-vous à votre compte</p>

            {/* Email */}
            <div className="mt-8">
              <label className="block text-white/70 typo-small mb-2" htmlFor="email">
                Email
              </label>
              <Input
                tone="light"
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email.value}
                onChange={handleEmailChange}
              />
            </div>

            {/* Password */}
            <div className="mt-5">
              <div className="flex justify-between items-center mb-2">
                <label className="text-white/70 typo-small" htmlFor="password">
                  Mot de passe
                </label>
                <Link to="/forgot-password" className="text-white/40 typo-small hover:text-white/60 transition-colors">
                  Mot de passe oublié ?
                </Link>
              </div>
              <Input
                tone="light"
                id="password"
                type="password"
                placeholder="••••••••"
                value={password.value}
                onChange={handlePasswordChange}
              />
            </div>

            {/* Submit */}
            <div className="mt-8">
              <Button
                type="submit"
                variant="primary"
                tone="light"
                size="lg"
                className="w-full"
                disabled={!email.value || !password.value}
                loading={loginMutation.isPending}
              >
                Se connecter
              </Button>
            </div>

            {/* Signup link */}
            <p className="mt-6 text-center text-white/70 typo-small">
              Pas encore de compte ?{' '}
              <Link to="/signup" className="text-white/90 font-medium hover:underline">
                S'inscrire
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="hidden lg:flex lg:w-[65%] relative bg-secondary-900 border-l border-secondary-800 overflow-hidden lg:flex-col items-center justify-center">
        {/* Background image */}
        <img
          src="/signin-background.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/15" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full w-full gap-12">
          <h2 className="text-white typo-display text-center leading-tight">
            <span className="block">{'Transformez vos stations en'}</span>
            <span className="block text-primary-500">{' produits connectés'}</span>
          </h2>
          <p className="text-white typo-h3 text-center">
            Une solution intégrée pour piloter vos stations à distance.
          </p>

          {/* Hero image */}
          <div className="flex justify-end -mt-32">
            <img
              src="/signin-lock.png"
              alt="Station Follow"
              className="w-[580px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
